import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ColumnKey, Complaint, ComplaintAppointment, ComplaintAppointmentColumnKey, ComplaintAppointmentWithComplaintDetails, ComplaintPosition, PersonsService, scrollTop } from '@icc/b2b/shared';
import { ComplaintService } from '../complaint.service';
import { Router } from '@angular/router';
import { ComplaintPartyListItem } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { DialogService } from '@icc/ui';
import { ComplaintUserPreviewDialogComponent } from '../complaint-user-preview-dialog/complaint-user-preview-dialog.component';
import { GoogleMapsLoaderService } from '../google-maps-loader.service';
import { TranslateService } from '@icc/shared';

@Component({
    selector: 'icc-complaint-appointment-list-table',
    templateUrl: './complaint-appointment-list-table.component.html',
    styleUrls: ['./complaint-appointment-list-table.component.scss']
})
export class ComplaintAppointmentListTableComponent {
    @Output() updateAppointments = new EventEmitter<boolean>();
    @Output() saveConfiguration = new EventEmitter<void>();
    @Output() setColumnSortOptions = new EventEmitter<void>();
    @Output() changeOrderBy = new EventEmitter<ComplaintAppointmentColumnKey>();

    @Input() filteredAppointments: ComplaintAppointmentWithComplaintDetails[] = [];
    @Input() orderedAppointments: ComplaintAppointmentWithComplaintDetails[] = [];
    @Input() viewComplaintOrderNumber = false;
    @Input() viewAssignedDistributor = false;
    @Input() viewAssignedAuthorizedRetailer = false;

    @Input() columnSortOptions: Partial<
        Record<
            ComplaintAppointmentColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};

    @Input() appointmentsInPage = 20;
    @Input() totalItems = 0;

    private _page = 1;
    @Input()
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }

    @Input() contractorsList: ComplaintPartyListItem[] = [];

    @Output() currentPageEmitter = new EventEmitter<number>();

    onChangePage(page: number) {
        this.page = page;
        this.currentPageEmitter.emit(page);
        this.saveConfiguration.emit();
        this.updateAppointments.emit(true);
        scrollTop();
    }

    constructor(
        private complaintService: ComplaintService,
        private personsService: PersonsService,
        private router: Router,
        private dialogService: DialogService,
        private googleMapsLoaderService: GoogleMapsLoaderService,
        private translateService: TranslateService,
    ) {}

    open(docId: string, appointmentUuid: string) {
        this.complaintService.openDocumentByUuid(docId);
        this.router.navigate(['trade', 'complaint', docId], { queryParams: { origin: 'appointment-list', appointmentUuid } });
    }

    orderBy(column: ComplaintAppointmentColumnKey) {
        this.changeOrderBy.emit(column);
    }

    trackByUuid(index: number, item: ComplaintAppointmentWithComplaintDetails): string {
        return item.uuid;
    }

    getWorkerName(id: number): string {
        return this.personsService.getWorkerByUserId(id)?.person.full_formal_name ?? '';
    }

    getUniqueDistributorOrderNumbers(appointment: ComplaintAppointmentWithComplaintDetails): string[] {
        return appointment.distributorOrderNumbers?.split(',') || [];
    }

    getAssignedDistributor(appointment: ComplaintAppointmentWithComplaintDetails): string {
        return appointment.distributorCompanyName || this.translateService.instant('INTERFACE|Brak');
    }

    getAssignedAuthorizedRetailer(
        appointment: ComplaintAppointmentWithComplaintDetails
    ): string {
        return appointment.authorizedRetailerCompanyName || this.translateService.instant('INTERFACE|Brak');
    }

    async openLocationDialog(appointment: ComplaintAppointmentWithComplaintDetails) {
        await this.complaintService.loaded;
        const apiKey = this.complaintService.data?.googleMapsApiKey;
        if (apiKey) {
            await this.googleMapsLoaderService.load(apiKey);
        }
        const apiLoaded = this.googleMapsLoaderService.isApiLoaded();

        const user = {
            referenceName: appointment.clientName || '',
            email: '',
            phoneNumber: '',
            address: appointment.installationAddress || '',
        };

        this.dialogService.open(ComplaintUserPreviewDialogComponent, {
            data: {
                user,
                userType: 'client',
                responsibleUser: null,
                assignedUser: null,
                locationDescription: appointment.locationDescription,
                locationSource: appointment.locationSource,
                locationPartialMatchConfirmed: appointment.locationPartialMatchConfirmed,
                latitude: appointment.latitude,
                longitude: appointment.longitude,
                apiLoaded,
            },
        });
    }
}
